"use client";

import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Heading3, Paragraph } from "../typography";
import { AustraliaMapMaskedCard } from "../australia-map-masked-card";
import Image from "next/image";

export const TheChallengeSolution = () => {
  useSectionNavTheme({
    trigger: "#act-challenge-solution",
    isDark: true,
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1024px)", () => {
      // Set initial opacity for cards 2 and 3, and solution content
      gsap.set([".australia-card-2", ".australia-card-3"], {
        opacity: 0,
      });

      // Initial reveal animation
      gsap.from(".challenge-title", {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: "#act-challenge-solution",
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // Pinned scroll animation
      const pinnedTl = gsap.timeline({
        ease: "none",
        scrollTrigger: {
          trigger: "#act-challenge-solution",
          start: "top top",
          end: "bottom 95%",
          pin: ".map-cards",
          scrub: 1,
          invalidateOnRefresh: true,
          // markers: true,
        },
      });

      // Reveal Australia map cards in sequence
      pinnedTl
        .to(".australia-card-1", {
          opacity: 0,
        })
        .to(
          ".australia-card-1 .map-card__content",
          {
            y: -300,
          },
          "<"
        )
        .to(
          ".australia-card-2",
          {
            opacity: 1,
          },
          "<"
        )
        .to(".australia-card-2 .map-card__content", {
          y: -300,
        })
        .to([".australia-card-3"], {
          opacity: 1,
        });
    });
  });

  return (
    <section
      id="act-challenge-solution"
      className="relative py-20 lg:py-32 xl:py-40"
    >
      <div className="absolute inset-0 dark:bg-gradient-to-l from-haizea-green/50 via-haizea-dark to-slate-700/60"></div>
      <div className="container">
        <div className="flex items-start flex-col lg:flex-row gap-10 lg:gap-20">
          <div className="map-cards @container relative rounded-2xl border border-slate-200 dark:border-slate-500 w-full lg:w-[600px] lg:max-w-1/2 max-h-[60vh] aspect-square shrink-0 bg-slate-50/10 backdrop-blur-sm">
            <AustraliaMapMaskedCard
              title="20 Years"
              subtitle="Assessment of urban expansion"
              className="australia-card-1 w-full h-full bg-gradient-to-br from-haizea-green via-90% via-haizea-orange  to-haizea-blue"
            />
            <AustraliaMapMaskedCard
              title="66"
              subtitle="Threatened Species"
              className="australia-card-2 w-full h-full bg-gradient-to-br from-haizea-green via-70% to-haizea-blue"
            />
            <AustraliaMapMaskedCard
              title="AI-driven"
              subtitle="Spatial analysis with Terrak.io"
              className="australia-card-3 w-full h-full bg-radial from-haizea-orange via-60% via-haizea-green to-haizea-blue"
            />
            <Image
              src="/icons/act.svg"
              alt="ACT Map icon"
              className="absolute z-10 top-[62%] left-[75%] @2xl:left-[73%] @3xl:left-[66%] object-cover w-4.5 h-9 drop-shadow-2xl/50 opacity-60"
              style={{ objectPosition: "center" }}
              priority
              draggable={false}
              width={18}
              height={36}
            />
          </div>
          <div className="relative w-full">
            <ChallengeContent />
            <SolutionContent />
          </div>
        </div>
      </div>
    </section>
  );
};

const ChallengeContent = () => {
  return (
    <div className="space-y-6 text-lg leading-relaxed dark:text-slate-300 bg-white/5 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none p-6 lg:p-0">
      <Heading3 className="challenge-title text-slate-800 dark:text-slate-200">
        The Challenge
      </Heading3>

      <div className="challenge-content space-y-4 lg:space-y-10">
        <Paragraph>
          The ACT Office of the Commissioner for Sustainability and Environment
          needed to understand the environmental impact of
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            {" "}
            two decades of urban growth around Canberra.
          </span>
        </Paragraph>
        <Paragraph>
          Traditional methods couldn&apos;t provide the comprehensive,
          quantitative analysis required to inform{" "}
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            critical policy decisions
          </span>{" "}
          about future development.
        </Paragraph>

        <Paragraph>
          The Commissioner was investigating whether planning legislation and
          environmental protections were{" "}
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            {" "}
            adequately protecting the ACT&apos;s unique ecosystems and
            threatened species{" "}
          </span>{" "}
          as the city expanded.
        </Paragraph>
        <Paragraph>
          With{" "}
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            66 threatened species and critically endangered ecological
            communities
          </span>{" "}
          at stake, they needed robust evidence to support recommendations to
          government.
        </Paragraph>
      </div>
    </div>
  );
};

const SolutionContent = () => {
  const solutionFeatures = [
    {
      title: "AI-enhanced environmental insights",
      description:
        "High-resolution analysis of urban growth, vegetation change, and ecosystem condition from 2004–2024.",
    },
    {
      title: "Threatened species intelligence",
      description: "Assessment of habitat changes affecting 29 key species.",
    },
    {
      title: "Dynamic data access",
      description: (
        <>
          An interactive web platform (
          <a
            href="https://act.terrak.io/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-haizea-green underline hover:no-underline"
          >
            https://act.terrak.io/
          </a>
          ) that puts 20 years of environmental intelligence at users’
          fingertips.
        </>
      ),
    },
    {
      title: "Public transparency",
      description:
        "Clear, accessible insights for government, researchers, and the community.",
    },
  ];

  return (
    <div className="mt-10 lg:mt-40 space-y-6 text-lg leading-relaxed dark:text-slate-300 bg-white/5 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none p-6 lg:p-0">
      <Heading3 className="text-slate-800 dark:text-slate-200">
        The Solution
      </Heading3>
      <div className="">
        <div className="solution-content space-y-4 lg:space-y-10">
          <Paragraph>
            Haizea Analytics developed a comprehensive assessment combining{" "}
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              AI-enhanced satellite analysis with the Terrak.io{" "}
            </span>
            platform to deliver unprecedented insights into urban expansion
            impacts from 2004-2024.
          </Paragraph>

          {solutionFeatures.map((feature, index) => (
            <div key={index} className="space-y-2">
              <h4 className="text-xl font-semibold text-emerald-600 dark:text-emerald-400">
                {feature.title}
              </h4>
              <Paragraph>{feature.description}</Paragraph>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
